import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpHeaders} from '@angular/common/http';

import {Observable} from 'rxjs/Observable';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {Subject} from 'rxjs/Subject';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/finally';

import {environment} from '../../../environments/environment';

import {DataService} from './data.service';
import {GridRow, OfferEntryData} from '../models/offerEntryData';
import {TranslateService} from '@ngx-translate/core';
import {Equipment} from '../../shared/models/equipment';
import {SpinnerService} from './spinner.service';
import {MessageData} from '../models/message-data';
import {UtilsService} from './utils.service';
import { Product } from '../../shared/models/product';

@Injectable()
export class NotificationService {

    constructor(
        private data: DataService,
        private translate: TranslateService,
        private spinnerService: SpinnerService,
        private utilsService: UtilsService
    ) {
    }

    private getHeader(): HttpHeaders {
        let headers = new HttpHeaders();
        const programId = sessionStorage.getItem('currentProgram');
        this.utilsService.debugLog(programId);
        if (programId) {
            headers = headers.set('Program', programId);
        }

        return headers;
    }

    getSMSContent(selectedProduct: Product, offerEntryData: OfferEntryData, gridRow?: GridRow): string {
        if (gridRow) {
            const curtailData = gridRow.dataRows.filter((dataRow) => (dataRow.metaDataKey === 'curtail_data'));
            let intervalTime = [];
            let curtailment = [];
            for (let interval of gridRow.clearedIntervals) {
                let idx = offerEntryData.intervalIds.indexOf(interval);
                if (offerEntryData.columnHeaders[idx] && curtailData[0].dataValues[idx] > 0) {
                    intervalTime.push(offerEntryData.columnHeaders[idx].split(':')[0]);
                    curtailment.push(curtailData[0].dataValues[idx]);
                    // when zero values should be sent out, please use the statement below and remove the condition curtailData[0].dataValues[idx] > 0
                    // curtailment.push((curtailData[0].dataValues[idx] > 0) ? curtailData[0].dataValues[idx] : 0);
                }
            }
            return this.makeSMSContent(selectedProduct, offerEntryData.startTime, intervalTime, curtailment);
        } else {
            return this.makeSMSContent(selectedProduct, offerEntryData.startTime);
        }
    }

    makeSMSContent(selectedProduct: Product, offerDate: Date, intervalTime?: string[], curtailment?: number[]): string {
        // Example Message :
        // 2018년 06월 08일 (Fri) 구간별 경제성DR 낙찰량(kWh) 통보
        // 16~17 : 3000
        // 구간별 CBL과 SMP는 Enel X 경제성 DR 메뉴를 참고해주시기 바랍니다.
        // if noClearedOffer
        // [에넬엑스]2월 7일(감축일) 경제성 DR 입찰 결과 유찰되었습니다. 참여해 주셔서 감사합니다.
        let content: string = '';

        if (intervalTime) {
            let message: string = this.translate.instant('notfication.offer-cleared-sms-text');
            message = message.replace('{product}', selectedProduct.name);
            content += this.getDateAsSMSFormatedString(offerDate) + ' ' + message + '\r\n';

            let hourContent: string = '';
            let zeroCheck: boolean = false;
            for (let i = 0; i < intervalTime.length; i++) {
                let numInterval: number = parseInt(intervalTime[i]);
                let from: string = (numInterval - 1) < 10 ? '0' + (numInterval - 1).toString() : (numInterval - 1).toString();
                let to: string = numInterval < 10 ? '0' + numInterval.toString() : numInterval.toString();
                let tmpHourContent: string = `${from}~${to}`;
                if (!curtailment[i] || curtailment[i] !== 0) {
                    zeroCheck = true;
                }
                hourContent += `${from}~${to} : ${curtailment[i]}` + '\r\n';
            }

            if (!zeroCheck) {
                hourContent += ' ' + this.translate.instant('notfication.offer-cleared-sms-text-check-true');
                content += hourContent;
            } else {
                content += hourContent;
                content += this.translate.instant('notfication.offer-cleared-sms-text-check-false');
            }
        } else {
            const month: string = (offerDate.getMonth() + 1).toString();
            const day: string = offerDate.getDate().toString();
            let message = this.translate.instant('notfication.no-cleared-sms-text');
            message = message.replace('{month}', month);
            message = message.replace('{day}', day);
            message = message.replace('{product}', selectedProduct.name);
            content += message;
        }
        return content;
    }

    private getDateAsSMSFormatedString(date: any): string {
        if (date) {
            let year: string = '';
            let month: string = '';
            let day: string = '';
            let dayName: string = '';
            if (date instanceof Date) {
                const dateObj = this.getDateAsObject(date);
                year = dateObj.year;
                month = dateObj.month;
                day = dateObj.day;
                dayName = dateObj.weekDay;
            } else {
                year = date.date.year;
                month = date.date.month.toLocaleString(undefined, {minimumIntegerDigits: 2, useGrouping: false});
                day = date.date.day.toLocaleString(undefined, {minimumIntegerDigits: 2, useGrouping: false});
                const tmpDate = new Date(`${year}-${month}-${day}`);
                dayName = this.getDateAsObject(tmpDate).weekDay;
            }

            return `${year}년 ${month}월 ${day}일 (${dayName})`;
        }
        return '';
    }

    private getDateAsObject(date: Date): any {
        const gsDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        if (date) {
            return {
                year: date.getFullYear().toString(),
                month: (date.getMonth() + 1) < 10 ? '0' + (date.getMonth() + 1) : (date.getMonth() + 1).toString(),
                day: (date.getDate() < 10) ? '0' + date.getDate() : date.getDate().toString(),
                weekDay: gsDayNames[date.getDay()]
            };
        }
        return null;
    }

    getContact(offerDate: Date, id: string, level: string): Observable<any> {
        this.spinnerService.show('offerSpinner');
        const dateObj = this.getDateAsObject(offerDate);
        const dateStr = `${dateObj.year}-${dateObj.month}-${dateObj.day}`;

        const endpoint = `${environment.contact_url}?date=${dateStr}&id=${id}&level=${level}`;

        return this.data.get(endpoint, this.getHeader()).map(data => {
            if (data['data'] != null) {
                return data['data'];
            }
            return data;
        }).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    sendNotification(recipientList: string[], text: string): Observable<any> {
        this.spinnerService.show('offerSpinner');
        const from: string = environment.common.sms_notification_from;

        let httpRequests = [];
        for(let to of recipientList) {
            const endpoint = encodeURI(`${environment.notification_url}?to=${to}&from=${from}&text=${text}`);

            httpRequests.push(this.data.get(endpoint, this.getHeader()).map((response) => ({
                success: true,
                response: (response['data'] != null ? response['data'] : response),
                recipient: to
            })).catch(error => (Observable.of({
                success: false,
                response: error,
                recipient: to
            }))));
        }

        return forkJoin(httpRequests).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/core/services/notification.service.ts